import * as ents from '@attain-apps/entities'
import * as excel from '@attain-apps/excel'
import { Workbook } from '@attain-apps/fe/web/main/lib/excel'
import { TypeChecker } from '@attain-apps/type-guards'

const SHEET_NAME_MAX_LENGTH = 31

/**
 * Creates new {@link Workbook} that presents template file for upload data to DMS
 */
export function generate(entities: ents.core.dms.models.Entity[]): Workbook {
  const workbook = new Workbook()

  const targetSheet = _generateTargetSheet(entities)
  workbook.addSheet(targetSheet)

  for (const entity of entities) {
    const entitySheet = _generateEntitySheet(entity)
    const entityDefinitionSheet = _generateEntityDefinitionSheet(entity)
    workbook.addSheet(entitySheet)
    workbook.addSheet(entityDefinitionSheet)
  }
  return workbook
}

/**
 * Target sheet (@) - first sheet in workbook
 */
function _generateTargetSheet(
  entities: ents.core.dms.models.Entity[],
): excel.Sheet {
  const sheet = new excel.Sheet('@')
  // Header row
  const headerRow = _generateRow(
    {
      description: 'Description',
      name: 'Name',
      sheet: 'sheet',
      target: 'target',
    },
    { isHeader: true },
  )
  sheet.addRow(headerRow)

  // Entity rows
  for (const entity of entities) {
    const row = _generateRow({
      description: entity.description ?? '',
      name: entity.name,
      sheet: _generateSheetName(entity.name),
      target: entity.id,
    })
    sheet.addRow(row)
  }
  return sheet

  /** Generate row, based on provided content and options */
  function _generateRow(
    content: {
      sheet: string
      target: string
      name: string
      description: string
    },
    options?: {
      isHeader?: boolean
    },
  ): excel.Row {
    return new excel.Row(
      _generateCell(content.sheet, { isHeader: options?.isHeader }),
      _generateCell(content.target, { isHeader: options?.isHeader }),
      excel.Cell.empty(),
      _generateCell(content.name, { isHeader: options?.isHeader }),
      _generateCell(content.description, {
        isHeader: options?.isHeader,
      }),
    )
  }
}

/**
 *  Generate sheet for entity
 */
function _generateEntitySheet(
  entity: ents.core.dms.models.Entity,
): excel.Sheet {
  const sheet = new excel.Sheet(_generateSheetName(entity.name))
  const row = new excel.Row()
  for (const attribute of entity.attributes ?? []) {
    const { partialId } = ents.core.dms.models.Id.Parts.from(attribute.id)

    // Intrinsic, Enum or Structure attribute - column: partial id
    if (
      ents.core.common.value.types.Intrinsic.isIntrinsic(attribute.type) ||
      ents.core.dms.data.Value.Type.Enum.isEnum(attribute.type) ||
      ents.core.dms.data.Value.Type.Structure.isStructure(attribute.type)
    ) {
      row.addCell(_generateCell(partialId, { isHeader: true }))
    }
    // Referenced attribute - columns: entityId, recordId
    else if (
      ents.core.dms.data.Value.Type.Reference.isReference(attribute.type)
    ) {
      row.addCell(_generateCell(`${partialId}.entityId`, { isHeader: true }))
      row.addCell(_generateCell(`${partialId}.recordId`, { isHeader: true }))
    }
  }

  // Parent column (^) for non root entities
  if (entity.level > 0) {
    row.addCell(_generateCell('^', { isHeader: true }))
  }

  sheet.addRow(row)
  return sheet
}

/** Generate Entity definition sheet */
function _generateEntityDefinitionSheet(
  entity: ents.core.dms.models.Entity,
): excel.Sheet {
  const sheet = new excel.Sheet(_generateSheetName(`@${entity.name}`))
  // Header row
  const headerRow = _generateRow(
    {
      attributeName: 'Attribute Name',
      columnName: 'Column Name',
      dataType: 'Data Type',
      defaultValue: 'Default Value',
      description: 'Description',
      isOptional: 'Is Optional',
    },
    { isHeader: true },
  )
  sheet.addRow(headerRow)

  const { attributes } = entity
  for (const attribute of attributes ?? []) {
    const row = _generateRow({
      attributeName: attribute.name,
      columnName: ents.core.dms.models.Id.Parts.from(attribute.id).partialId,
      dataType: _generateDataType(attribute.type),
      defaultValue: _generateDefaultValue(attribute.default),
      description: attribute.description ?? '',
      isOptional: attribute.isOptional ? 'true' : 'false',
    })
    sheet.addRow(row)
  }
  return sheet

  /** Generate row, based on provided content and options */
  function _generateRow(
    content: {
      columnName: string
      attributeName: string
      description: string
      dataType: string
      isOptional: string
      defaultValue: string
    },
    options?: {
      isHeader?: boolean
    },
  ): excel.Row {
    return new excel.Row(
      _generateCell(content.columnName, { isHeader: options?.isHeader }),
      _generateCell(content.attributeName, { isHeader: options?.isHeader }),
      _generateCell(content.description, { isHeader: options?.isHeader }),
      _generateCell(content.dataType, { isHeader: options?.isHeader }),
      _generateCell(content.isOptional, { isHeader: options?.isHeader }),
      _generateCell(content.defaultValue, { isHeader: options?.isHeader }),
    )
  }

  /** Generate data type based on attribute type */
  function _generateDataType(type: ents.core.dms.data.Value.Type): string {
    return ents.core.common.value.types.Intrinsic.isIntrinsic(type)
      ? type
      : ents.core.dms.data.Value.Type.Enum.isEnum(type)
        ? type.enumId
        : ents.core.dms.data.Value.Type.Reference.isReference(type)
          ? type.dimensionId
          : ents.core.dms.data.Value.Type.Structure.isStructure(type)
            ? type.structureId
            : ''
  }

  /** Generate excel default value based on attribute default value */
  function _generateDefaultValue(
    value: ents.core.dms.models.Attribute['default'],
  ): string {
    if (TypeChecker.isString(value)) {
      return value.replace(/"/g, '')
    }
    return ''
  }
}

/**
 * Generate sheet name based on Entity name.
 * Sheet name cannot have empty spaces or special characters.
 * Excel sheet name can't be longer
 * than 31 characters. In case that original sheet name exceed 31 characters it
 * will be shorten
 */
function _generateSheetName(entityName: string): string {
  return entityName
    .replace(/[\s*?:\\/[\]]/g, '')
    .slice(0, SHEET_NAME_MAX_LENGTH)
}

/** Generate header cell - set bold text*/
function _generateCell(
  text: string,
  opts?: { isHeader?: boolean },
): excel.Cell {
  return new excel.Cell({
    richText: [{ font: { bold: !!opts?.isHeader }, text }],
  })
}
